import { useEffect, useMemo, useState, FormEvent } from "react";
import { api, mensajeError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOpciones } from "../hooks/useOpciones";
import { PageHeader } from "../components/ui/PageHeader";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";
import { Trash2, Plus, Minus, ShoppingCart, PackagePlus, Search, X, ShoppingBag } from "lucide-react";

interface Linea { productoId: string; nombre: string; precio: number; cantidad: number; }

const categoriasProducto = [
  { value: "SERVICIO", label: "Servicio" },
  { value: "MEDICAMENTO", label: "Medicamento" },
  { value: "ALIMENTO", label: "Alimento" },
  { value: "ACCESORIO", label: "Accesorio" },
  { value: "OTRO", label: "Otro" },
];

// Color de acento por categoría, solo para que el catálogo se distinga de
// un vistazo (igual que en Mascotas) — no tiene ningún otro significado.
const ACENTO_CATEGORIA: Record<string, { franja: string; texto: string; fondo: string }> = {
  SERVICIO: { franja: "bg-blue-400", texto: "text-blue-600", fondo: "bg-blue-50" },
  MEDICAMENTO: { franja: "bg-rose-400", texto: "text-rose-600", fondo: "bg-rose-50" },
  ALIMENTO: { franja: "bg-amber-400", texto: "text-amber-600", fondo: "bg-amber-50" },
  ACCESORIO: { franja: "bg-violet-400", texto: "text-violet-600", fondo: "bg-violet-50" },
  OTRO: { franja: "bg-slate-300", texto: "text-slate-500", fondo: "bg-slate-100" },
};

const productoVacio = { nombre: "", categoria: "SERVICIO", precioVenta: "", stockActual: "0" };

export default function PuntoDeVenta() {
  const { soloLectura } = useAuth();
  const { opciones: clientes } = useOpciones("/clientes", (c) => c.nombre);

  const [productos, setProductos] = useState<any[]>([]);
  const [cargandoProductos, setCargandoProductos] = useState(true);

  const [clienteId, setClienteId] = useState("");
  // Buscador del catálogo: en vez de desplegar los 200 productos en un
  // select gigante, se escribe para filtrar y se da clic en la tarjeta (o
  // Enter) para agregarla al carrito — como en cualquier punto de venta.
  const [busqueda, setBusqueda] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState<string | null>(null);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Catálogo para el punto de venta: da de alta productos físicos (con
  // control de stock) y también servicios (baño premium, vacuna tipo 1,
  // etc.) que no manejan inventario, todo bajo el mismo catálogo.
  const [modalProductoAbierto, setModalProductoAbierto] = useState(false);
  const [formProducto, setFormProducto] = useState<any>(productoVacio);
  const [guardandoProducto, setGuardandoProducto] = useState(false);
  const [errorProducto, setErrorProducto] = useState<string | null>(null);

  async function cargarProductos() {
    setCargandoProductos(true);
    try {
      const res = await api.get("/productos", { params: { porPagina: 200 } });
      setProductos(res.data.items ?? []);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setCargandoProductos(false);
    }
  }

  useEffect(() => {
    cargarProductos();
  }, []);

  const q = busqueda.trim().toLowerCase();
  const productosFiltrados = useMemo(() => {
    return productos.filter((p) => {
      const coincideCategoria = !categoriaFiltro || p.categoria === categoriaFiltro;
      const coincideBusqueda = !q || p.nombre.toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q);
      return coincideCategoria && coincideBusqueda;
    });
  }, [productos, categoriaFiltro, q]);

  const cantidadesEnCarrito = useMemo(() => {
    const mapa: Record<string, number> = {};
    for (const l of lineas) mapa[l.productoId] = l.cantidad;
    return mapa;
  }, [lineas]);

  const totalArticulos = lineas.reduce((acc, l) => acc + l.cantidad, 0);
  const total = lineas.reduce((acc, l) => acc + l.precio * l.cantidad, 0);

  function agregarProducto(p: any, cantidad = 1) {
    if (soloLectura || !p) return;
    setLineas((prev) => {
      const existente = prev.find((l) => l.productoId === p.id);
      if (existente) {
        return prev.map((l) => (l.productoId === p.id ? { ...l, cantidad: l.cantidad + cantidad } : l));
      }
      return [...prev, { productoId: p.id, nombre: p.nombre, precio: p.precioVenta, cantidad }];
    });
  }

  // Enter en el buscador agrega de un jalón el primer resultado que
  // coincide — útil para ir tecleando y dando Enter rápido sin usar el
  // mouse (o si algún día se conecta un lector de código de barras, que
  // "escribe" el SKU y manda Enter solo).
  function alPresionarEnterBusqueda(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (productosFiltrados.length === 0) return;
    agregarProducto(productosFiltrados[0]);
    setBusqueda("");
  }

  function quitarLinea(productoId: string) {
    if (soloLectura) return;
    setLineas((prev) => prev.filter((l) => l.productoId !== productoId));
  }

  // Ajusta la cantidad de una línea ya agregada al carrito, sin tener que
  // quitarla y volver a agregarla. Si llega a 0, se quita del carrito.
  function cambiarCantidadLinea(productoId: string, cantidad: number) {
    if (soloLectura) return;
    if (cantidad <= 0) {
      quitarLinea(productoId);
      return;
    }
    setLineas((prev) => prev.map((l) => (l.productoId === productoId ? { ...l, cantidad } : l)));
  }

  function vaciarCarrito() {
    if (soloLectura || lineas.length === 0) return;
    if (!confirm("¿Vaciar todo el carrito?")) return;
    setLineas([]);
  }

  async function cobrar() {
    if (soloLectura || lineas.length === 0) return;
    setProcesando(true);
    setError(null);
    setMensaje(null);
    try {
      await api.post("/ventas", {
        clienteId: clienteId || undefined,
        metodoPago,
        items: lineas.map((l) => ({
          productoId: l.productoId,
          descripcion: l.nombre,
          cantidad: l.cantidad,
          precioUnitario: l.precio,
        })),
      });
      setMensaje("Venta registrada correctamente.");
      setLineas([]);
      setClienteId("");
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setProcesando(false);
    }
  }

  function abrirNuevoProducto() {
    if (soloLectura) return;
    setFormProducto(productoVacio);
    setErrorProducto(null);
    setModalProductoAbierto(true);
  }

  async function guardarProducto(e: FormEvent) {
    e.preventDefault();
    setGuardandoProducto(true);
    setErrorProducto(null);
    try {
      const res = await api.post("/productos", {
        nombre: formProducto.nombre,
        categoria: formProducto.categoria,
        precioVenta: Number(formProducto.precioVenta) || 0,
        stockActual: Number(formProducto.stockActual) || 0,
      });
      setModalProductoAbierto(false);
      await cargarProductos();
      // Lo agregamos ya directo al carrito, listo para cobrarse.
      agregarProducto(res.data);
    } catch (err) {
      setErrorProducto(mensajeError(err));
    } finally {
      setGuardandoProducto(false);
    }
  }

  return (
    <div>
      <PageHeader
        titulo="Punto de venta"
        descripcion="Busca un producto o servicio y da clic para agregarlo al carrito."
        icono={<ShoppingCart size={20} />}
        acciones={
          !soloLectura && (
            <button className="btn-secondary" onClick={abrirNuevoProducto}>
              <PackagePlus size={16} /> Nuevo producto/servicio
            </button>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 card p-5 space-y-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9 pr-9"
              placeholder="Busca por nombre o SKU... (Enter agrega el primer resultado)"
              disabled={soloLectura}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={alPresionarEnterBusqueda}
              autoFocus
            />
            {busqueda && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                onClick={() => setBusqueda("")}
                title="Limpiar búsqueda"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            <button
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                categoriaFiltro === null ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
              onClick={() => setCategoriaFiltro(null)}
            >
              Todos
            </button>
            {categoriasProducto.map((c) => {
              const acento = ACENTO_CATEGORIA[c.value];
              const activo = categoriaFiltro === c.value;
              return (
                <button
                  key={c.value}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    activo ? `${acento.franja} text-white` : `${acento.fondo} ${acento.texto} hover:opacity-80`
                  }`}
                  onClick={() => setCategoriaFiltro(activo ? null : c.value)}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {cargandoProductos ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : productos.length === 0 ? (
            <EmptyState
              titulo="Todavía no tienes productos ni servicios"
              descripcion='Usa "Nuevo producto/servicio" arriba para dar de alta el primero (por ejemplo: Baño premium, Vacuna tipo 1, etc.).'
            />
          ) : productosFiltrados.length === 0 ? (
            <EmptyState titulo="Sin resultados" descripcion={`No encontramos nada para "${busqueda}". Intenta con otro nombre.`} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[520px] overflow-y-auto pr-1">
              {productosFiltrados.map((p) => {
                const acento = ACENTO_CATEGORIA[p.categoria] ?? ACENTO_CATEGORIA.OTRO;
                const enCarrito = cantidadesEnCarrito[p.id] ?? 0;
                const manejaStock = p.categoria !== "SERVICIO";
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={soloLectura}
                    onClick={() => agregarProducto(p)}
                    className={`entrada-contenido group relative flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <div className={`h-1.5 w-full ${acento.franja}`} />
                    {enCarrito > 0 && (
                      <span className="absolute right-2 top-3 flex h-6 min-w-6 items-center justify-center rounded-full bg-brand-500 px-1.5 text-[11px] font-semibold text-white shadow-sm">
                        {enCarrito}
                      </span>
                    )}
                    <div className="flex flex-1 flex-col gap-1.5 p-3">
                      <span className={`w-fit rounded-full px-2 py-0.5 text-[10px] font-medium ${acento.fondo} ${acento.texto}`}>
                        {categoriasProducto.find((c) => c.value === p.categoria)?.label ?? p.categoria}
                      </span>
                      <p className="text-sm font-medium leading-snug text-slate-700">{p.nombre}</p>
                      <div className="mt-auto flex items-end justify-between pt-1">
                        <p className="font-semibold text-slate-800">${p.precioVenta.toLocaleString("es-MX")}</p>
                        {manejaStock && (
                          <p className={`text-[11px] ${p.stockActual > 0 ? "text-slate-400" : "text-red-400"}`}>
                            {p.stockActual > 0 ? `Stock: ${p.stockActual}` : "Sin stock"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-1 border-t border-slate-100 bg-slate-50/60 py-1.5 text-[11px] font-medium text-brand-600 opacity-0 transition-opacity group-hover:opacity-100">
                      <Plus size={12} /> Agregar
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="card p-5 h-fit space-y-4 lg:sticky lg:top-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              <ShoppingBag size={18} /> Carrito
              {totalArticulos > 0 && (
                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-600">
                  {totalArticulos} {totalArticulos === 1 ? "artículo" : "artículos"}
                </span>
              )}
            </h3>
            {lineas.length > 0 && (
              <button className="text-xs text-red-400 hover:text-red-600 hover:underline" disabled={soloLectura} onClick={vaciarCarrito}>
                Vaciar
              </button>
            )}
          </div>

          <div>
            <label className="label">Cliente (opcional)</label>
            <select className="input" disabled={soloLectura} value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">Público en general</option>
              {clientes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="divide-y divide-slate-50 border border-slate-100 rounded-xl max-h-72 overflow-y-auto">
            {lineas.length === 0 && (
              <p className="p-4 text-center text-sm text-slate-400">
                Busca a la izquierda y da clic en una tarjeta para empezar la venta.
              </p>
            )}
            {lineas.map((l) => (
              <div key={l.productoId} className="flex items-center justify-between gap-2 p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">{l.nombre}</p>
                  <p className="text-xs text-slate-400">${l.precio.toLocaleString("es-MX")} c/u</p>
                </div>
                <div className="flex items-center gap-1 rounded-lg border border-slate-200 shrink-0">
                  <button
                    className="flex h-7 w-7 items-center justify-center text-slate-500 hover:text-slate-700 disabled:opacity-40"
                    disabled={soloLectura}
                    title="Quitar una pieza"
                    onClick={() => cambiarCantidadLinea(l.productoId, l.cantidad - 1)}
                  >
                    <Minus size={13} />
                  </button>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="h-7 w-10 border-x border-slate-200 text-center text-sm outline-none"
                    disabled={soloLectura}
                    value={l.cantidad}
                    onChange={(e) => cambiarCantidadLinea(l.productoId, Math.round(Number(e.target.value)) || 0)}
                  />
                  <button
                    className="flex h-7 w-7 items-center justify-center text-slate-500 hover:text-slate-700 disabled:opacity-40"
                    disabled={soloLectura}
                    title="Agregar una pieza"
                    onClick={() => cambiarCantidadLinea(l.productoId, l.cantidad + 1)}
                  >
                    <Plus size={13} />
                  </button>
                </div>
                <button className="text-red-400 hover:text-red-600 shrink-0" disabled={soloLectura} onClick={() => quitarLinea(l.productoId)}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>

          <div>
            <label className="label">Método de pago</label>
            <select className="input" disabled={soloLectura} value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
              <option value="EFECTIVO">Efectivo</option>
              <option value="TARJETA">Tarjeta</option>
              <option value="TRANSFERENCIA">Transferencia</option>
            </select>
          </div>
          <div className="flex justify-between text-lg font-bold text-slate-800 border-t border-slate-100 pt-3">
            <span>Total</span>
            <span>${total.toLocaleString("es-MX")}</span>
          </div>
          {mensaje && <p className="text-sm text-green-600">{mensaje}</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            className="btn-primary w-full justify-center"
            disabled={soloLectura || procesando || lineas.length === 0}
            onClick={cobrar}
            title={soloLectura ? "El negocio está en modo de solo lectura" : undefined}
          >
            {procesando ? "Procesando..." : "Cobrar"}
          </button>
        </div>
      </div>

      <Modal open={modalProductoAbierto} onClose={() => setModalProductoAbierto(false)} title="Nuevo producto o servicio">
        <form onSubmit={guardarProducto} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input
              className="input"
              required
              placeholder="Ej. Baño premium, Vacuna tipo 1"
              value={formProducto.nombre}
              onChange={(e) => setFormProducto({ ...formProducto, nombre: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Categoría</label>
            <select
              className="input"
              value={formProducto.categoria}
              onChange={(e) => setFormProducto({ ...formProducto, categoria: e.target.value })}
            >
              {categoriasProducto.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Precio de venta *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input"
                required
                value={formProducto.precioVenta}
                onChange={(e) => setFormProducto({ ...formProducto, precioVenta: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Stock inicial</label>
              <input
                type="number"
                min="0"
                step="1"
                className="input"
                value={formProducto.stockActual}
                onChange={(e) => setFormProducto({ ...formProducto, stockActual: e.target.value })}
              />
              <p className="text-xs text-slate-400 mt-1">Déjalo en 0 si es un servicio (no maneja inventario).</p>
            </div>
          </div>

          {errorProducto && <p className="text-sm text-red-600">{errorProducto}</p>}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={() => setModalProductoAbierto(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardandoProducto}>
              {guardandoProducto && <Spinner className="h-4 w-4 text-white" />}
              Guardar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
